import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzDashboardMacroComponentWebComponent } from '../../gzDashboardMacroComponent';
import {
  GetUserConfigForDashboardResponse,
  InitGenericDashboardResponse,
  ViewType,
} from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { store } from '../../../redux/store';
import { setBulkInitialState } from '../../redux/dashboards.app.reducer';
import { GzImageRepoWebComponent } from '../../../ui/gzImageRepo';
import { ImageState } from '../../../ui/gzImageRepo/types';
import { Status, Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { createNewSmartView } from '../../redux/dashboards.smart.views.slice';
import { DisplayMode } from '../../../types/common/types';
import { GzDashboardAppWebComponent } from '../component/GzDashboardAppWebComponent';

export class GzDashboardAppView extends BaseWebComponentView {
  private static readonly CUSTOM_VIEW_LABEL = 'Custom view';

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
  }

  buildSuccessView(response: InitGenericDashboardResponse) {
    this.dom.$wrapper.innerHTML = '';

    if (response.status === Status.NO_COMPANIES_IN_TARGETS || !response.data) {
      this.buildNoCompaniesInTargetsView();
      return;
    }

    store.dispatch(setBulkInitialState(response.data as GetUserConfigForDashboardResponse));
    const $gzDashboardMacroComponent = new GzDashboardMacroComponentWebComponent();
    $gzDashboardMacroComponent.dashboardType = response.data.dashboardType;

    const gzGenericDashboardAppElement = (document.querySelector(
      `gz-generic-dashboard-app[data-dashboard-type="${response.data.dashboardType}"]`
    ) as GzDashboardAppWebComponent | null);
    if (!gzGenericDashboardAppElement) {
      this.buildErrorView();
      return;
    }

    $gzDashboardMacroComponent.displayMode = gzGenericDashboardAppElement.displayMode;
    $gzDashboardMacroComponent.selectedTarget = gzGenericDashboardAppElement.selectedTarget;

    $gzDashboardMacroComponent.injectDependencies(this.dependencies);
    this.dom.$wrapper.appendChild($gzDashboardMacroComponent);

    if (!response.data.uiConfig?.smartViewsPanel?.visible && $gzDashboardMacroComponent.displayMode === DisplayMode.DASHBOARD) {
      const hasCustomView = response.data.smartViews.some(smartView => smartView.type === ViewType.CUSTOM);
      if (!hasCustomView) {
        store.dispatch(createNewSmartView(GzDashboardAppView.CUSTOM_VIEW_LABEL));
      }
    }
  }

  private buildErrorTemplate(): void {
    const errorTemplate = new GzImageRepoWebComponent();
    errorTemplate.state = ImageState.ERROR;
    errorTemplate.innerHTML = `
            <gz-title fontWeight='500' fontSize='20' class="error-text title">${this.dependencies.translateFn(
              'A general system error occurred.'
            )}</gz-title>
            <gz-text fontSize='13' class="error-text subtitle">${this.dependencies.translateFn(
              'Please contact Bitdefender Enterprise Support for more information.'
            )}</gz-text>
    `;

    this.dom.$wrapper.appendChild(errorTemplate);
  }

  buildNoCompaniesInTargetsView() {
    const emptyTemplate = new GzImageRepoWebComponent();
    emptyTemplate.state = ImageState.EMPTY;
    emptyTemplate.innerHTML = `
      <gz-title fontWeight='500' fontSize='20' class="error-text title">${this.dependencies.translateFn(
        'No results were found'
      )}</gz-title>
      <gz-text fontSize='13' class="dashboard-error--message">${this.dependencies.translateFn(
        'The network group you have access to has no available companies. Please contact your company administrator for more information.'
      )}</gz-text>
    `;

    this.dom.$wrapper.appendChild(emptyTemplate);
  }

  buildErrorView() {
    this.dom.$wrapper.innerHTML = '';
    this.buildErrorTemplate();
  }

  buildLoadingTemplate(): void {
    this.dom.$wrapper.innerHTML = '';
    const loadingTemplate = document.createElement('div');
    loadingTemplate.classList.add('loading-template');
    loadingTemplate.innerHTML = `<gz-push-body-layout
        .isOpen="${true}"
      >
        <div slot="push-body-sidenav-title" class="border">
         <gz-loader rows="1" class="smartview-title-loader"></gz-loader>
        </div>

        <div slot="push-body-sidenav">
          <div id="dashboard-smart-views" class="border">
            <div class="loader pr-2 pl-2">
              <gz-loader rows="4" class="smartview-loader"></gz-loader>
              <gz-loader rows="4" class="smartview-loader pt-2"></gz-loader>
            </div>
          </div>
        </div>

        <div slot="push-body-content" id="fullscreen-content">
         <div id="dashboard-feedback-toolbar" class="feedback-toolbar">
            <gz-loader rows="1" class="title-loader"></gz-loader>
          </div>
          <div id="dashboard-toolbar" class="border">
            <gz-loader rows="1" class="title-loader pt-1"></gz-loader>
            <div class="action-bar-container d-flex flex-row align-items-center">
              <gz-loader rows="1" class="actions-loader mr-1"></gz-loader>
              <div class="separator"></div>
              <div class="buttons-loader-container d-flex">
                <gz-loader rows="1" class="icon-loader"></gz-loader>
                <gz-loader rows="1" class="icon-loader"></gz-loader>
                <gz-loader rows="1" class="icon-loader"></gz-loader>
                <gz-loader rows="1" class="icon-loader"></gz-loader>
              </div>
            </div>
          </div>
          <div id="dashboard-filters" class="separator">
            <div class="d-flex flex-row filter-loader-container">
              <gz-loader rows="1" columns="1" class="filter-loader"></gz-loader>
              <gz-loader rows="1" columns="1" class="filter-loader ml-2"></gz-loader>
            </div>
          </div>
          <div id="dashboard-grid-stack" class="border">
            <div class="dashboard-loading d-flex justify-center align-items-center">
              <gz-progress-spinner class="spinner-dashboard" indeterminate="" spinnerwidth="large"></gz-progress-spinner>
            </div>
          </div>
        </div>
      </gz-push-body-layout>`;

    this.dom.$wrapper.appendChild(loadingTemplate);
  }
}
